import '@zoom/common-header/style.css'

const WAFFLE_SELECTOR = '[data-zoom-waffle-menu]'
const HEADER_SELECTOR = '[data-zoom-common-header]'
const PROFILE_SELECTOR = '[data-zoom-profile-menu]'

let sdkLoadPromise
const HOST_TO_WEB_BASE_URL = {
  'www.zoom.com': 'https://zoom.us',
  'stage.zoom.com': 'https://zoomdev.us',
  'int.zoom.com': 'https://dev-integration.zoomdev.us',
  'int-optimizely.zoom.com': 'https://dev-integration.zoomdev.us',
  'ade.zoom.com': 'https://dev-integration.zoomdev.us'
}

// Small helper: read one cookie value by its name. Returns '' if not found.
const getCookieValue = (name) => {
  if (typeof document === 'undefined' || !document.cookie) return ''
  const allCookies = document.cookie.split(';')
  for (const rawCookie of allCookies) {
    const [key, ...rest] = rawCookie.trim().split('=')
    if (key === name) return decodeURIComponent(rest.join('='))
  }
  return ''
}

const normalizeBaseUrl = (value) => {
  const trimmed = (value || '').trim()
  if (!trimmed) return ''
  return trimmed.startsWith('http://') || trimmed.startsWith('https://')
    ? trimmed
    : `https://${trimmed}`
}

// Work out which Zoom backend URL the SDK should talk to. It tries, in order:
// the value on the HTML element, then the zm_web_domain cookie, then our map above.
const resolveWebBaseUrl = (target) => {
  if (target?.dataset?.zoomWebBaseUrl) {
    return normalizeBaseUrl(target.dataset.zoomWebBaseUrl)
  }

  const cookieDomain = getCookieValue('zm_web_domain')
  if (cookieDomain) return normalizeBaseUrl(cookieDomain)

  if (typeof window !== 'undefined' && window.location?.hostname) {
    const mappedWebBaseUrl = HOST_TO_WEB_BASE_URL[window.location.hostname]
    if (mappedWebBaseUrl) return mappedWebBaseUrl
  }

  return ''
}

const getCmsGuid = () => {
  const value = getCookieValue('_zm_cms_guid')
  return value || undefined
}

export const isLikelyAnonymous = () => {
  const cmsGuid = getCookieValue('_zm_cms_guid')
  return !cmsGuid || cmsGuid === 'false'
}

export const expireCmsGuidCookie = () => {
  if (typeof document === 'undefined') return
  const expire = '=; Max-Age=-99999999; path=/;'
  document.cookie = `_zm_cms_guid${expire} domain=.zoom.com`
  document.cookie = `_zm_cms_guid${expire} domain=zoom.com`
  document.cookie = `_zm_cms_guid${expire} domain=${window.location.hostname}`
  document.cookie = `_zm_cms_guid${expire}`
}

// Download the SDK code only once and reuse it. If the download fails we return
// null instead of crashing.
const loadCommonHeaderSdk = async () => {
  if (!sdkLoadPromise) {
    sdkLoadPromise = import('@zoom/common-header')
      .catch(() => null)
  }

  return sdkLoadPromise
}

// Write a small status word onto the element (handy for debugging in the page).
const setStatus = (el, status) => {
  if (el) el.dataset.zoomHeaderStatus = status
}

export const revealLegacyTrailing = () => {
  const trailing = document.querySelector(
    '.nav-utils__group--trailing.is-sdk-pending'
  )
  if (trailing) trailing.classList.remove('is-sdk-pending')
}

export const hideLegacyTrailing = () => {
  const trailing = document.querySelector(
    '.nav-utils__group--trailing:not(.is-sdk-pending)'
  )
  if (trailing) trailing.classList.add('is-sdk-pending')
}

export const revealLegacyProfile = () => {
  const profileFallback = document.querySelector(
    '.nav-utils__group--profile.is-sdk-fallback'
  )
  if (profileFallback) profileFallback.classList.remove('is-sdk-fallback')
}

export const hideLegacyProfile = () => {
  const profileFallback = document.querySelector(
    '.nav-utils__group--profile:not(.is-sdk-fallback)'
  )
  if (profileFallback) profileFallback.classList.add('is-sdk-fallback')
}

const revealLegacyFallback = (target) => {
  const sdkContainer = target?.closest?.('.nav-utils__group--common-header-sdk')
  if (!sdkContainer || sdkContainer.dataset.zoomFallbackActive === 'true') return
  sdkContainer.dataset.zoomFallbackActive = 'true'
  sdkContainer.style.display = 'none'

  revealLegacyProfile()
  revealLegacyTrailing()
}

const hideLegacyFallback = () => {
  hideLegacyProfile()
  hideLegacyTrailing()
}

// Build the standalone waffle menu.
// If it cannot mount, it shows the old menu instead.
export const initCommonHeaderWaffle = () => {
  // If the full header is on the page, it already owns the waffle, so skip.
  if (document.querySelector(HEADER_SELECTOR)) return Promise.resolve(null)

  const target = document.querySelector(WAFFLE_SELECTOR)
  if (!target) return Promise.resolve(null)

  if (target.dataset.zoomWaffleMounted === 'true') {
    if (target.dataset.zoomHeaderStatus === 'mounted') hideLegacyTrailing()
    return Promise.resolve(null)
  }
  target.dataset.zoomWaffleMounted = 'true'
  const webBaseUrl = resolveWebBaseUrl(target)
  const cmsGuid = getCmsGuid()

  return loadCommonHeaderSdk().then((sdk) => {
    if (!sdk?.createWaffleMenu) {
      target.dataset.zoomWaffleMounted = 'false'
      setStatus(target, 'sdk-missing')
      return null
    }

    return sdk.createWaffleMenu(target, {
      webBaseUrl,
      ...(cmsGuid && { cmsGuid }),
      useDark: false,
      systemFonts: true,
      meetingShortcut: true,
      productNameForTracking: 'zoom.com'
    }).then((result) => {
      if (result?.supported === false) {
        setStatus(target, 'unsupported')
        revealLegacyFallback(target)
      } else if (result) {
        setStatus(target, 'mounted')
        hideLegacyFallback()
      }
      return result
    }).catch(() => {
      target.dataset.zoomWaffleMounted = 'false'
      setStatus(target, 'init-error')
      revealLegacyFallback(target)
      return null
    })
  })
}

// Build the standalone profile menu (the user avatar menu).
// If it cannot mount, it shows the old profile menu instead.
export const initCommonHeaderProfile = () => {
  // If the full header is on the page, it already owns the profile menu, so skip.
  if (document.querySelector(HEADER_SELECTOR)) return Promise.resolve(null)

  const target = document.querySelector(PROFILE_SELECTOR)
  if (!target) return Promise.resolve(null)

  if (target.dataset.zoomProfileMounted === 'true') {
    if (target.dataset.zoomHeaderStatus === 'mounted') hideLegacyProfile()
    return Promise.resolve(null)
  }
  target.dataset.zoomProfileMounted = 'true'
  const webBaseUrl = resolveWebBaseUrl(target)
  const cmsGuid = getCmsGuid()

  return loadCommonHeaderSdk().then((sdk) => {
    if (!sdk?.createProfileMenu) {
      target.dataset.zoomProfileMounted = 'false'
      setStatus(target, 'sdk-missing')
      return null
    }

    return sdk.createProfileMenu(target, {
      webBaseUrl,
      ...(cmsGuid && { cmsGuid }),
      useDark: false,
      systemFonts: true,
      enableSwitchUserLogin: false,
      productNameForTracking: 'zoom.com',
      beforeSignOut: () => {
        expireCmsGuidCookie()
        return Promise.resolve()
      }
    }).then((result) => {
      if (result?.supported === false) {
        setStatus(target, 'unsupported')
        revealLegacyFallback(target)
      } else if (result) {
        setStatus(target, 'mounted')
        hideLegacyFallback()
      }
      return result
    }).catch(() => {
      target.dataset.zoomProfileMounted = 'false'
      setStatus(target, 'init-error')
      revealLegacyFallback(target)
      return null
    })
  })
}

// Build the FULL header (logo + waffle + profile together). Used on Admin
// Center style pages. Does nothing if that header is not on the page.
export const initCommonHeader = () => {
  const target = document.querySelector(HEADER_SELECTOR)
  if (!target) return Promise.resolve(null)

  if (target.dataset.zoomHeaderMounted === 'true') return Promise.resolve(null)
  target.dataset.zoomHeaderMounted = 'true'
  const webBaseUrl = resolveWebBaseUrl(target)
  const cmsGuid = getCmsGuid()

  const logoText = target.dataset.zoomLogoText || 'Admin Center'
  const logoLink = target.dataset.zoomLogoLink || '/'
  const logoLinkTarget = target.dataset.zoomLogoLinkTarget || '_self'
  const brandingLogoUrl = target.dataset.zoomBrandingLogoUrl || ''
  const brandingLogoLink = target.dataset.zoomBrandingLogoLink || ''
  const brandingAlt = target.dataset.zoomBrandingAlt || ''
  const meetingShortcutTarget = target.dataset.zoomMeetingShortcutTarget || '_self'
  const disableWaffle = target.dataset.zoomDisableWaffle === 'true'

  const options = {
    webBaseUrl,
    ...(cmsGuid && { cmsGuid }),
    productNameForTracking: 'zoom.com',
    systemFonts: true,
    useDark: false,
    logoText,
    logoLink,
    logoLinkTarget,
    waffleMenu: disableWaffle
      ? false
      : {
        meetingShortcut: {
          show: true,
          target: meetingShortcutTarget
        }
      },
    profileMenu: {
      useDark: false,
      systemFonts: true,
      enableSwitchUserLogin: false
    }
  }

  if (brandingLogoUrl) {
    options.brandingLogoUrl = brandingLogoUrl
    options.brandingLogoLink = brandingLogoLink
    options.brandingAlt = brandingAlt
    options.brandingLinkTarget = '_blank'
  }

  return loadCommonHeaderSdk().then((sdk) => {
    if (!sdk?.createHeader) {
      target.dataset.zoomHeaderMounted = 'false'
      setStatus(target, 'sdk-missing')
      return null
    }

    return sdk.createHeader(target, options)
      .then((result) => {
        if (result?.supported === false) {
          setStatus(target, 'unsupported')
        } else if (result) {
          setStatus(target, 'mounted')
          hideLegacyFallback()
        }
        return result
      })
      .catch(() => {
        target.dataset.zoomHeaderMounted = 'false'
        setStatus(target, 'init-error')
        return null
      })
  })
}
